import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type SupportedLanguage = 'en' | 'pt-BR'

export interface PlayerPreferences {
  showTurnBreakdownModal: boolean
  showTourWelcomeModal: boolean // AGC: v6.9.0 - Tour welcome modal on startup
  soundEffectsEnabled: boolean // AGC: v6.10.1 - Sound effects toggle
  musicEnabled: boolean // AGC: v6.10.1 - Background music toggle
  language: SupportedLanguage
}

interface PlayerPreferencesContextValue {
  preferences: PlayerPreferences
  updatePreferences: (updates: Partial<PlayerPreferences>) => void
  isLoaded: boolean
}

const STORAGE_KEY = 'playerPreferences'

const defaultPreferences: PlayerPreferences = {
  showTurnBreakdownModal: true,
  showTourWelcomeModal: true, // AGC: v6.9.0 - Show tour welcome modal by default
  soundEffectsEnabled: true, // AGC: v6.10.1 - Sound effects enabled by default
  musicEnabled: true, // AGC: v6.10.1 - Music enabled by default
  language: 'en',
}

const PlayerPreferencesContext = createContext<PlayerPreferencesContextValue | undefined>(undefined)

export const PlayerPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<PlayerPreferences>(defaultPreferences)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsLoaded(true)
      return
    }

    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as PlayerPreferences
        setPreferences({
          ...defaultPreferences,
          ...parsed,
        })
      }
    } catch (error) {
      console.warn('⚠️ AGC: Failed to load player preferences, using defaults.', error)
    } finally {
      setIsLoaded(true)
    }
  }, [])

  const updatePreferences = useCallback((updates: Partial<PlayerPreferences>) => {
    setPreferences((previous) => {
      const next = { ...previous, ...updates }
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        }
      } catch (error) {
        console.warn('⚠️ AGC: Failed to persist player preferences.', error)
      }
      return next
    })
  }, [])

  const value = useMemo<PlayerPreferencesContextValue>(
    () => ({
      preferences,
      updatePreferences,
      isLoaded,
    }),
    [preferences, updatePreferences, isLoaded],
  )

  return <PlayerPreferencesContext.Provider value={value}>{children}</PlayerPreferencesContext.Provider>
}

export const usePlayerPreferences = (): PlayerPreferencesContextValue => {
  const context = useContext(PlayerPreferencesContext)
  if (!context) {
    throw new Error('usePlayerPreferences must be used within a PlayerPreferencesProvider')
  }
  return context
}

